import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import API_BASE from "../utils/api";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const percentageColor = (pct) => (pct >= 75 ? "text-green-600" : pct >= 50 ? "text-yellow-600" : "text-red-600");

const downloadBlob = (content, type, filename) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Monthly attendance report + audit trail, shared between pages/admin/Attendance.jsx
// and pages/principal/Attendance.jsx (both surface GET /attendance/monthly-report
// and GET /activity-logs?module=Attendance behind an identical "Reports & Audit" tab).
const AttendanceReportsTab = ({ classes }) => {
  const now = new Date();
  const [classId, setClassId] = useState(classes?.[0]?._id || "");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    if (!classId && classes?.length) setClassId(classes[0]._id);
  }, [classes, classId]);

  const fetchReport = useCallback(async () => {
    if (!classId) return;
    setReportLoading(true);
    setReportError("");
    try {
      const res = await axios.get(`${API_BASE}/attendance/monthly-report?classId=${classId}&month=${month}&year=${year}`);
      setReport(res.data);
    } catch (err) {
      setReportError(err.response?.data?.error || "Failed to load report");
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  }, [classId, month, year]);

  const fetchAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/activity-logs?module=Attendance&limit=20`);
      setAuditLogs(res.data.logs || []);
    } catch (_) {
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const exportCsv = () => {
    if (!report) return;
    const rows = [["Roll No", "Name", "Present", "Late", "Half Day", "Absent", "On Leave", "Days Marked", "Percentage"]];
    report.data.forEach((d) => rows.push([d.rollNumber, d.name, d.present, d.late, d.halfDay, d.absent, d.onLeave, d.totalDaysMarked, `${d.percentage}%`]));
    downloadBlob(rows.map((r) => r.join(",")).join("\r\n"), "text/csv", `${report.title.replace(/\s+/g, "_")}.csv`);
  };

  const exportJson = () => {
    if (!report) return;
    downloadBlob(JSON.stringify(report, null, 2), "application/json", `${report.title.replace(/\s+/g, "_")}.json`);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Monthly Attendance Report</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <select value={classId} onChange={(e) => setClassId(e.target.value)} className="border rounded-md px-3 py-2">
            {classes?.map((c) => <option key={c._id} value={c._id}>Class {c.className} - Section {c.section}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="border rounded-md px-3 py-2">
            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded-md px-3 py-2">
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="flex gap-2">
            <button onClick={exportCsv} disabled={!report} className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50">CSV</button>
            <button onClick={exportJson} disabled={!report} className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50">JSON</button>
          </div>
        </div>

        {reportError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm mb-4">{reportError}</div>}

        {reportLoading ? (
          <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
        ) : report ? (
          <>
            <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-600">
              <span>Class Teacher: <strong>{report.summary.classTeacher}</strong></span>
              <span>Total Students: <strong>{report.summary.totalStudents}</strong></span>
              <span>Average Attendance: <strong className={percentageColor(report.summary.averagePercentage)}>{report.summary.averagePercentage}%</strong></span>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Roll No</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Present</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Late</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Half Day</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Absent</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">On Leave</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.data.map((d) => (
                    <tr key={d.studentId}>
                      <td className="px-4 py-2">{d.rollNumber}</td>
                      <td className="px-4 py-2 font-medium">{d.name}</td>
                      <td className="px-4 py-2 text-center">{d.present}</td>
                      <td className="px-4 py-2 text-center">{d.late}</td>
                      <td className="px-4 py-2 text-center">{d.halfDay}</td>
                      <td className="px-4 py-2 text-center">{d.absent}</td>
                      <td className="px-4 py-2 text-center">{d.onLeave}</td>
                      <td className={`px-4 py-2 text-center font-semibold ${percentageColor(d.percentage)}`}>{d.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-400 text-center py-6">No data for this selection.</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Attendance Audit Log</h2>
        {auditLoading ? (
          <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
        ) : auditLogs.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No attendance activity recorded yet.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {auditLogs.map((log) => (
              <div key={log._id} className="border-b pb-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-800">{log.action.replace(/_/g, " ")}</span>
                  <span className="text-xs text-gray-400">{new Date(log.createdAt).toLocaleString("en-IN")}</span>
                </div>
                <p className="text-xs text-gray-500">{log.details}</p>
                <p className="text-xs text-gray-400">{log.userName} ({log.role})</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceReportsTab;
